import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { Locales, TLocale } from '@@config/locale';
import { LocaleContext, TLocaleContext } from '@@contexts/LocaleContext';

/** Normalize SDK locale value (e.g. 'en', 'ja') to reportcheck TLocale (e.g. en_US, ja_JP). */
const SDK_TO_RC_LOCALE: Record<string, TLocale> = {
    en: Locales.en_US,
    ja: Locales.ja_JP,
};

function normalizeLocaleParam(param: string | null): TLocale | null {
    if (!param) return null;
    if (param in Locales) return param as TLocale;
    if (param in SDK_TO_RC_LOCALE) return SDK_TO_RC_LOCALE[param];
    return null;
}

export const getLocaleFromUrl = (search?: string): TLocale => {
    const query = search ?? (typeof window !== 'undefined' ? window.location.search : '');
    if (query) {
        const params = new URLSearchParams(query);
        const normalized = normalizeLocaleParam(params.get('locale'));
        if (normalized) return normalized;
    }
    return Locales.en_US;
};

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const [locale, setLocale] = useState<TLocale>(() => getLocaleFromUrl(location.search));

    useEffect(() => {
        setLocale(getLocaleFromUrl(location.search));
    }, [location.search]);

    const localeContext: TLocaleContext = {
        locale: locale,
        updateLocale: (newLocale: TLocale): void => {
            setLocale(newLocale);
        },
    };

    return <LocaleContext.Provider value={localeContext}>{children}</LocaleContext.Provider>;
};
